import { useFormContext, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';

import styled from 'styled-components';

import {
    DeliveryState,
    DetailedOrderFragmentFragment,
    OrderInvoicingStatus,
    OrderState,
    useChargeOrderMutation,
    TotadminPermission,
} from 'data/__generated__';

import { canChargeOrder, getInvoicingLabel } from 'pages/Orders/OrderDetails/order-invoicing-display';

import {
    FIRST_ORDER,
    getFrequencyLabel,
    getServiceLabel,
    PUNCTUAL,
    PUNCTUAL_ORDER,
    RECURRING,
} from 'pages/Orders/constants';
import { DAYS_OF_THE_WEEK } from 'constants/dateAndTime';

import { SectionColumn, SectionContainer, ColumnsSectionContainer } from 'components/DetailsView/Section';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import {
    DetailFormCheckbox,
    DetailFormDatePicker,
    DetailFormSelect,
    DetailFormValue,
    DetailLink,
    DetailValue,
} from 'components/DetailsView';
import { CopyValue } from 'components/CopyValue';
import { StatusTag } from 'pages/Orders/components/StatusTag';

import { dateFromString, formatDateAsAnniversary } from 'helpers/dateTimes';
import { ORDER_STATES_LABELS } from 'pages/Orders/constants/states';

function possibleStates(state: OrderState): OrderState[] {
    switch (state) {
        case OrderState.Created:
            return [OrderState.Created, OrderState.InProgress, OrderState.Archived];
        case OrderState.InProgress:
            return [OrderState.InProgress, OrderState.Validated, OrderState.Archived];
        case OrderState.Validated:
            return [OrderState.Validated, OrderState.Ready, OrderState.Archived];
        case OrderState.Ready:
            return [OrderState.Ready, OrderState.Paused, OrderState.Archived];
        case OrderState.Paused:
            return [OrderState.Paused, OrderState.Ready, OrderState.Archived];
        case OrderState.Archived:
            return [OrderState.Archived];
        case OrderState.Deleted:
            // Terminal : la suppression logique passe par l'action « Supprimer », pas par l'éditeur d'état.
            return [OrderState.Deleted];
    }
}

function getStateOptions(currentState: OrderState) {
    return possibleStates(currentState).map((state) => ({
        value: state,
        label: ORDER_STATES_LABELS[state],
    }));
}

export const GeneralInfoSection = ({ order }: { order: DetailedOrderFragmentFragment }) => {
    const {
        _id: orderId,
        appreciation,
        badService,
        delivery,
        deliveryDate,
        deliveryTimeWindows,
        feedback,
        frequency,
        hasOnsiteSetup,
        invoicing,
        isPunctual,
        number,
        organization: { _id: organizationId, hasSubscription, name: organizationName, paymentType },
        previous_orderId: previousOrderId,
        reference,
        state,
        totem: { name: totemName, number: totemNumber },
        user: {
            _id: userId,
            profile: { firstname, lastname },
        },
    } = order;

    const [chargeOrder, { loading: chargeOrderLoading }] = useChargeOrderMutation({
        variables: { orderId },
        onCompleted: (data) => {
            // La mutation renvoie une RELECTURE de la commande : on lit donc l'état d'ARRIVÉE, pas celui d'avant
            // le clic. Le statut est la seule preuve que l'API a agi — un `Pending` inchangé signifie qu'elle a
            // refusé (hors périmètre), et c'est bien une erreur à annoncer.
            const status = data.chargeOrder.invoicing.status;
            if (status === OrderInvoicingStatus.Paid) {
                toast.success('Commande facturée et encaissée !');
            } else if (status === OrderInvoicingStatus.Invoiced) {
                toast.success('Commande facturée !');
            } else if (status === OrderInvoicingStatus.ToBeInvoiced) {
                // Org non facturée au jour : la commande est inscrite au registre et partira sur le cycle mensuel.
                toast.info('Commande enregistrée : elle sera facturée sur le prochain cycle de facturation.');
            } else {
                toast.error("Une erreur est survenue, la commande n'a pas pu être facturée.");
            }
        },
        onError: () => {
            toast.error("Une erreur est survenue, la commande n'a pas pu être facturée.");
        },
    });

    const { control, register } = useFormContext();
    const watchDeliveryDate: string = useWatch({
        control,
        name: 'deliveryDate',
        defaultValue: deliveryDate,
    });
    const today = new Date();

    const getTranslatedReasons = () => {
        const translatedReasons = badService?.map((service: string) => getServiceLabel(service)) || [];
        return translatedReasons.join(', ');
    };

    const dayNumber = dateFromString(watchDeliveryDate).getDay();
    const index = dayNumber === 0 ? 6 : dayNumber - 1;
    const translatedWeekday = DAYS_OF_THE_WEEK[index]?.label || '';

    const hasBeenDelivered =
        delivery?.stateHistory.some((stateHistoryItem) => stateHistoryItem.state === DeliveryState.Delivered) ?? false;
    const invoicingLabel = getInvoicingLabel({ status: invoicing.status, paymentType, hasBeenDelivered });

    return (
        <SectionContainer title="Informations générales" isInitiallyOpen>
            <ColumnsSectionContainer numberOfColumns={4}>
                <SectionColumn>
                    <CopyValue value={orderId} />
                    <DetailValue label="N° de commande" value={number} />
                    <DetailValue
                        label="Type"
                        value={isPunctual ? PUNCTUAL : frequency ? getFrequencyLabel(frequency) : RECURRING}
                    />
                    {previousOrderId && ![FIRST_ORDER, PUNCTUAL_ORDER].includes(previousOrderId) ? (
                        <DetailLink label="Id de commande précédente" path="order" value={previousOrderId} />
                    ) : (
                        <DetailValue label="Id de commande précédente" value="-" />
                    )}
                    {delivery?._id ? (
                        <DetailLink label="Id de livraison" path="delivery" value={delivery?._id} />
                    ) : (
                        <DetailValue label="Id de livraison" value="Pas de livraison associée" />
                    )}
                    {invoicing.odooInvoiceUrl ? (
                        <DetailValue
                            label="Facture Odoo"
                            value={
                                <ExternalLink href={invoicing.odooInvoiceUrl} target="_blank" rel="noopener noreferrer">
                                    {invoicing.odooInvoiceId ? `Facture #${invoicing.odooInvoiceId}` : 'Ouvrir'}
                                </ExternalLink>
                            }
                        />
                    ) : null}
                    {invoicing.stripePaymentUrl ? (
                        <DetailValue
                            label="Paiement Stripe"
                            value={
                                <ExternalLink
                                    href={invoicing.stripePaymentUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Ouvrir le paiement
                                </ExternalLink>
                            }
                        />
                    ) : null}
                    {invoicingLabel ? <DetailValue {...invoicingLabel} /> : null}
                    {canChargeOrder({
                        status: invoicing.status,
                        hasBeenDelivered,
                    }) ? (
                        <TotemPrimaryButton
                            type="button"
                            disabled={chargeOrderLoading}
                            onClick={() => chargeOrder()}
                            requiredPermissions={[TotadminPermission.OrdersCharge]}
                        >
                            Facturer la commande
                        </TotemPrimaryButton>
                    ) : null}
                </SectionColumn>
                <SectionColumn>
                    <DetailLink label="Entreprise" path="organization" name={organizationName} value={organizationId} />
                    <DetailValue label="Totem" value={`Totem N° ${totemNumber} ${totemName ? `- ${totemName}` : ''}`} />
                    <DetailLink label="Admin" path="user" name={`${firstname} ${lastname}`} value={userId} />
                    <StatusTag state={state} showHeading />
                    <DetailFormSelect<OrderState>
                        label="Statut"
                        options={getStateOptions(state)}
                        defaultValue={state}
                        name="state"
                    />
                    <DetailFormValue
                        label="Référence"
                        sublabel="Reprise sur la facture (ex. société commanditaire — sites Wojo)"
                        type="text"
                        defaultValue={reference ?? ''}
                        {...register('reference', { required: false })}
                    />
                </SectionColumn>
                <SectionColumn>
                    <DetailFormDatePicker
                        label="Date de livraison"
                        placeholder="Date de commande"
                        defaultValue={deliveryDate}
                        name="deliveryDate"
                        control={control}
                        transformValueToDate={(value) => (value ? dateFromString(value) : null)}
                        transformDateToValue={(date) => (date ? formatDateAsAnniversary({ dateTime: date }) : null)}
                        isDisabled
                        minDate={today}
                    />
                    <DetailValue label="Jour de la semaine" value={translatedWeekday} />
                    <DetailValue
                        label="Créneau de livraison"
                        value={`${deliveryTimeWindows[0]?.start ?? '?'} - ${deliveryTimeWindows[0]?.stop ?? '?'}`}
                    />
                    <DetailValue
                        label="Frais de livraison"
                        sublabel="Frais de livraison de base"
                        value={`${hasSubscription ? 0 : 10} €`}
                    />
                    <DetailFormCheckbox
                        label="Mise en place"
                        sublabel={hasSubscription ? '' : '+10 € de frais de livraison'}
                        name="hasOnsiteSetup"
                        defaultChecked={hasOnsiteSetup}
                        disabled={!!delivery}
                    />
                </SectionColumn>
                <SectionColumn>
                    <DetailValue label="Note" value={appreciation ? `${Number(appreciation)}/5` : '-'} />
                    {!!appreciation && Number(appreciation) < 5 ? (
                        <>
                            <DetailValue label="Raison(s)" value={getTranslatedReasons()} />
                            <DetailValue label="Commentaires" value={feedback} />
                        </>
                    ) : null}
                </SectionColumn>
            </ColumnsSectionContainer>
        </SectionContainer>
    );
};

const ExternalLink = styled.a`
    margin-top: 5px;
    color: ${({ theme }) => theme.ctaPrimaryHoveredColor};
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
`;
